import type { QueryClient } from "@tanstack/solid-query";

/**
 * Shared helpers for optimistically updating the TanStack Query cache after a
 * detail-row mutation (create/update), so the list table shows fresh values
 * immediately on navigation back — even if a route loader re-seeds stale data.
 *
 * Both helpers only touch queries whose data has the paginated shape
 * `{ items: T[] }` (the list resources). Other shapes (e.g. flat arrays like
 * the "all accounts" resource) are left untouched.
 */

type PageShape<T> = { items: T[] };

function isPageShape<T>(old: unknown): old is PageShape<T> {
  return (
    !!old &&
    typeof old === "object" &&
    "items" in old &&
    Array.isArray(old.items)
  );
}

/**
 * Optimistically update a single item across every list query variant that
 * matches a query-key prefix (all pages, sorts, searches).
 */
export function updateListItemInCache<T extends { id: string }>(
  qc: QueryClient,
  queryKeyPrefix: readonly unknown[],
  id: string,
  patch: Partial<T>,
): void {
  qc.setQueriesData({ queryKey: queryKeyPrefix }, (old: unknown): unknown => {
    if (!isPageShape<T>(old)) return old;
    return {
      ...old,
      items: old.items.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    };
  });
}

/**
 * Optimistically add a new item to every list query variant that matches a
 * query-key prefix. Prepends the item (default views sort by createdAt desc);
 * a background refetch corrects ordering for non-default sorts.
 */
export function addListItemToCache<T extends { id: string }>(
  qc: QueryClient,
  queryKeyPrefix: readonly unknown[],
  item: T,
): void {
  qc.setQueriesData({ queryKey: queryKeyPrefix }, (old: unknown): unknown => {
    if (!isPageShape<T>(old)) return old;
    if (old.items.some((existing) => existing.id === item.id)) return old;
    return { ...old, items: [item, ...old.items] };
  });
}
